import { API } from './Auth';
import defaultRules from './SamplingRules/default-rules';
import { 
  test_query,
  query_for_search_id_and_type,
  docs_for_searchid_and_table, 
  type_count_for_searchid_by_table,
  full_entity_table_type_aggregation,
  match_searchid,
  has_entities,
  bucket_by_table,
  bucket_by_entity_type,
  sort_paginate_by_table,
  entity_type_exists,
  entity_type_nested_agg,
  query_docs_by_searchid
} from './api-queries';
import { 
  getDisplayId,
  getFirstKeyValue,
  getTablesFromMetaHits,
  getGlueTargetData,
  getRowsProcessed,
  transformTableEntityFields,
  getSearchState
} from 'Utils';
import {
  listSamplingRuleTemplates,
  templateResponseToUiRule,
  saveRulesList,
} from './DataDiscovery/samplingRulesApi';
import {
  listDataSourcesWithLastTest,
  toRequestDto,
  toUiDataSource,
  getDataSourceNumericId,
  createDataSource,
  updateDataSource as putStoredDataSource,
  deleteDataSourceById,
  getDataSourceById,
  getLastDataSourceTest,
  runDataSourceTest,
  testDtoToUiSummary,
  listDataSources,
} from './DataSources/datasourcesApi';
import { maybePersistSchedulePlaceholder, mergeScheduleIntoUiRow } from './DataSources/schedulePlaceholder';
import {
  getOverview,
  getJobResults,
  extractTablesFromPayload,
} from './DataDiscovery/discoverApi';

const getScheduledJobs = async () => {
  // console.log('getScheduledJobs');
  let results = [];

  try {
    const api = 'gwapi';
    const path = '/schedule';
    const response = await API.get(api, path);
    const jobs = response.scheduledJobs || [];
    jobs.forEach((job) => {
      const name = job.name || '';
      const slicedName = name.slice(3);
      job.name = slicedName;
    });
    results = [...jobs];
  } catch(e) {
    console.log(e);
  }

  return results;
}

const getScheduledJobsMap = (jobs) => {
  // console.log('getScheduledJobsMap');
  let result = {};

  try {
    jobs.forEach((job) => {
      const name = job.name;
      result[name] = job;
    })
  } catch(e) {
    console.log(e);
  }

  return result;
}

// Data Source calls (discover-api /api/datasources).
const getDataSources = async (pageSize, pageKey) => {
  try {
    return await listDataSourcesWithLastTest();
  } catch (e) {
    console.log(e);
    throw e;
  }
}

const getLastConnection = (connections) => {
  // console.log('getLastConnection');
  const lastConnection = connections.find((connection) => 
    connection.name === localStorage.getItem('lastConnection')) || connections[0];

  return lastConnection;
}

const saveDataSource = async (connection, id) => {
  const body = toRequestDto(connection);
  const numericId = getDataSourceNumericId(connection);
  let responseDto;
  if (numericId != null) {
    responseDto = await putStoredDataSource(numericId, body);
  } else {
    responseDto = await createDataSource(body);
  }
  // Persist schedule after save so we have the data source id for new sources
  const savedRow = { ...connection, id: responseDto.id };
  await maybePersistSchedulePlaceholder(savedRow);
  localStorage.setItem('lastConnection', connection.name);
  return toUiDataSource(responseDto);
}

// Include sampleRules arg if saving with schedule data
// const createDataSource = async (connection, sampleRules) => {
//   let data = {};

//   const scheduleData = {
//     scheduleId: `C2_${connection.name}`,
//     ...connection.scheduleExpression && sampleRules && ({
//       scheduleExpression: `${connection.scheduleExpression}`,
//       scheduleTargetData: getGlueTargetData(connection, sampleRules)
//     })
//   };

//   data = {
//     ...scheduleData,
//     ...connection,
//     ...connection.schemaName && ({ schemaName: connection.schemaName }),
//     ...connection.warehouse && ({ warehouse: connection.warehouse })
//   };

//   try {
//     let response = {};
//     const api = 'gwapi';
//     const path = '/datasource';
//     const params = { body: data };
//     response = await API.post(api, path, params);    
//     localStorage.setItem('lastConnection', connection.name);
//     return response;
//   } catch(e) {
//     console.log(e);
//     throw e;
//   }
// }

// Include sampleRules arg if saving with schedule data
const updateDataSource = async (connection, sampleRules) => {
  // console.log('updateDataSource');
  let data = {};

  const {
    scheduleExpression,
    scheduleTargetData,
    ...restOfConnection
  } = connection;

  data = scheduleExpression && sampleRules ? (
    {
      ...connection,
      scheduleTargetData: getGlueTargetData(connection, sampleRules),
      scheduleId: `C2_${connection.name}`
    }
  ) : (
    {
      ...restOfConnection
    }
  );

  try {
    let response = {};
    const api = 'gwapi';
    const path = '/datasource';
    const params = { body: data };
    response = await API.put(api, path, params);
    return response;
  } catch(e) {
    console.log(e);
    throw e;
  }
}

const testDataSource = async (source) => {
  try {
    const cid = getDataSourceNumericId(source);
    if (cid == null) {
      throw new Error('Data source must be saved before running a test.');
    }
    const dto = await runDataSourceTest(cid);
    return testDtoToUiSummary(dto, source.name);
  } catch (e) {
    console.log(e);
    throw e;
  }
}

const deleteDataSource = async (source) => {
  try {
    const cid = getDataSourceNumericId(source);
    if (cid == null) {
      throw new Error('Cannot delete: missing data source id. Reload the data sources list and try again.');
    }
    await deleteDataSourceById(cid);
  } catch (e) {
    console.log(e);
    throw e;
  }
}

// Sample Rules calls.
const getRulesList = async () => {
  try {
    const templates = await listSamplingRuleTemplates();
    if (!templates.length) {
      const result = [...defaultRules];
      if (result.length > 0) {
        result[0].last = true;
      }
      result.forEach((ruleSet, i) => {
        ruleSet.index = i;
      });
      return result;
    }
    const mapped = templates.map(templateResponseToUiRule).filter(Boolean);
    mapped.sort((a, b) => {
      if (a.builtIn && !b.builtIn) return -1;
      if (!a.builtIn && b.builtIn) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
    if (mapped.length > 0) {
      mapped.forEach((r) => {
        r.last = false;
      });
      mapped[0].last = true;
    }
    mapped.forEach((ruleSet, i) => {
      ruleSet.index = i;
    });
    return mapped;
  } catch (e) {
    console.log(e);
    throw e;
  }
};


const getLastRules = (rulesList) => {
  // console.log('getLastRules');
  const lastRules = rulesList.find((rules) => 
    rules.id === localStorage.getItem('lastRules')) || rulesList[0];

  return lastRules;
}

const saveRules = async (rules) => {
  try {
    await saveRulesList(rules);
    return { ok: true };
  } catch (e) {
    console.log(e);
    throw e;
  }
};

/** Discover job results only — same shape as legacy ES agg for `transformOverviewData`. */
const getOverviewData = async (searchid) => {
  const { tables } = await getOverview(searchid);
  const entityTotals = {};
  (tables || []).forEach((table) => {
    (table.fields || []).forEach((field) => {
      const findings = field && field.findings && typeof field.findings === 'object' ? field.findings : {};
      Object.entries(findings).forEach(([type, val]) => {
        const n = Number(val);
        entityTotals[type] = (entityTotals[type] || 0) + (Number.isFinite(n) ? n : 1);
      });
    });
  });
  const buckets = Object.entries(entityTotals).map(([key, count]) => ({
    key,
    count: { value: count },
  }));
  return {
    aggregations: {
      overview: {
        entities: { buckets },
      },
    },
  };
}

// ElasticSearch calls.
const getMetaData = async(searchid) => {
  // console.log('getMetaData');
  const tables = [];
  let searchAfter = null;
  let i = 0;

  try {
    const tableMetadataQuery = {
      "size": 100,
      "query": {
        "bool": {
          "should": [
            {
              "match_phrase": {
                "id" : searchid
              }
            },
            {
              "match_phrase": {
                "parent_searchid" : searchid
              }
            }
          ]
        }
      },
      "collapse": {
        "field": "table.keyword",
      },
      ...searchAfter && ({ "search_after": searchAfter }),
      "sort": [
        { "table.keyword": "desc" }
      ]
    }
    let response =  await API.post('gwapi','/elasticsearch/tables/_search', {
      body: tableMetadataQuery
      // queryStringParameters: {
      //   source: JSON.stringify(tableMetadataQuery),
      //   source_content_type: 'application/json'
      // }
    });
    // console.log('getMetaData response', response);

    if (response && response.error) {
      throw new Error(response.error.reason || 'Error fetching ElasticSearch data.');
    }

    let tableHits = [];
    
    if (response.hits && response.hits.hits) {
      tableHits = await response.hits.hits.map( value => value._source);
      return tableHits;
    }
    

  } catch (error) {
    console.log('getMetaData err', error);
    throw error;
  }
  return tables;
}


const getTableMetaData = async(searchid, table) => {
  // console.log('getTableMetaData called with:', searchid, table);
  try {
    const testQuery = await query_docs_by_searchid(searchid, table);
    let testResponse = await API.post('gwapi', '/elasticsearch/discovery/_search', {
      body: testQuery
    });
    // console.log('testResponse received:', searchid, table, testResponse);
  } catch (error) {
    console.error('Error in getTableMetaData test query:', error);
    throw error;
  }


  // console.log('getTableMetaData');
  const tables = [];
  let searchAfter = null;
  let i = 0;
  try {
    const specificTableMetadataQuery = {
      "size": 100,
      "query": {
        "bool": {
          "must": [
            {
              "match": {
                "table": table
              }
            }
          ],
          "should": [
            {
              "match_phrase": {
                "id": searchid
              }
            },
            {
              "match_phrase": {
                "parent_searchid": searchid
              }
            }
          ]
    }}}
    let response =  await API.post('gwapi','/elasticsearch/tables/_search', {
      body: specificTableMetadataQuery
      // queryStringParameters: {
      //   source: JSON.stringify(specificTableMetadataQuery),
      //   source_content_type: 'application/json'
      // }
    });
    // console.log('Barchart getTableMetaData response', response);

    if (response && response.error) {
      throw new Error(response.error.reason || 'Error fetching ElasticSearch data.');
    }

    let tableHits = [];
    
    if (response.hits && response.hits.hits) {
      tableHits = await response.hits.hits.map( value => value._source).filter(source => {

        // console.log(source.parent_searchid,searchid,source.table,table)
        return ((source.parent_searchid === searchid || source.id === searchid) && source.table === table)
        
      } );
      // console.log('tableHits', tableHits);
      return tableHits;
    }
    

  } catch (error) {
    console.log('getMetaData err', error);
    throw error;
  }
  return tables;
}



const getFoundEntities = async (searchid, table) => {

  // console.log('getFoundEntities');
  let result = { [table]: {} };
  let meta = {};
  try {
    const query = {
      "size": 0,
      "query": {
        "bool": {
          "should": [
            {
              "match_phrase": {
                "id": searchid
              }
            },
            {
              "match_phrase": {
                "parent_searchid": searchid
              }
            }
          ]
        }
      },
      "aggs": {
        "table": {
          "terms": {
            "field": "table.keyword",
            "include": [table]
          },
          "aggs": {
            "table_hit": {
              "top_hits": {
                "size": 1
              }
            }
          }
        }
      }
    }
    let response =  await API.post('gwapi','/elasticsearch/tables/_search', {
      body: query
      // queryStringParameters: {
      //   source: JSON.stringify(query),
      //   source_content_type: 'application/json'
      // }
    });
    // console.log('getFoundEntities meta response', response);
    if (response && response.error) {
      throw new Error(result.error.reason || 'Error fetching ElasticSearch data.');
    }

    const buckets = response && response.aggregations && response.aggregations.table && response.aggregations.table.buckets || [];
    const tableHits = buckets[0] && buckets[0].table_hit && buckets[0].table_hit.hits && buckets[0].table_hit.hits.hits;
    const source = (tableHits[0] && tableHits[0]._source) || {};
    result.rows_count = (source.meta && source.meta.rows_count) || 0;
    const columns = source.columns || [];
    meta[table] = columns;
  } catch(e) {
    console.log(e);
  }

  try {
    const query = {
      "size": 0,
      "query": {
        "bool": {
          "should": [
            {
              "match_phrase": {
                "search": searchid
              }
            },
            {
              "match_phrase": {
                "parent_searchid": searchid
              }
            }
          ]
        }
      },
      "aggs": {
        "table": {
          "terms": {
            "field": "table",
            "include": [table]
          },
          "aggs": {
            "sensitive_columns": {
              "nested": {
                "path": "entities"
              },
              "aggs": {
                "sensitive_columns": {
                  "terms": {
                    "field": "entities.Column",
                    "size": 1000
                  },
                  "aggs": {
                    "entities": {
                      "terms": {
                        "field": "entities.type",
                        "size": 100,
                      },
                      "aggs": {
                        "count": {
                          "cardinality": {
                            "field": "entities.unique_findings_key.hash",
                            "precision_threshold": 100
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    };
    let response =  await API.post('gwapi','/elasticsearch/discovery/_search', {
      body: query
      // queryStringParameters: {
      //   source: JSON.stringify(query),
      //   source_content_type: 'application/json'
      // }
    });
    // console.log('getFoundEntities discovery response', response);
    // console.log('getFoundEntities discovery response', response);
    const buckets = response && response.aggregations && response.aggregations.table && response.aggregations.table.buckets || [];
    const sensitiveColumns = buckets[0] && buckets[0].sensitive_columns && buckets[0].sensitive_columns.sensitive_columns && buckets[0].sensitive_columns.sensitive_columns.buckets || [];
    // const columns = tableHits[0] && tableHits[0]._source && tableHits[0]._source.columns || [];

    sensitiveColumns.forEach((col) => {
      const colNum = col.key;
      const colName = meta[table] && meta[table][colNum] && meta[table][colNum].Field;
      const entities = col && col.entities && col.entities.buckets || [];

      if (colName) {
        result[table][colName]= {
          entities: {}
        };
        entities.forEach((entity) => {
          const entityType = entity.key;
          const count = entity && entity.count && entity.count.value;
          result[table][colName].entities = {
            ...result[table][colName].entities,
            [entityType]: count
          }
        });
      }
    });
  } catch(e) {
    console.log(e);
  }
  // console.log('getFoundEntities result', result);
  return result;
}

const getTotalSensitiveTableCount = async (searchid) => {
  const { tables } = await getOverview(searchid);
  if (!Array.isArray(tables)) return 0;
  let count = 0;
  tables.forEach((t) => {
    const has = (t.fields || []).some((f) => f.findings && Object.keys(f.findings).length > 0);
    if (has) count += 1;
  });
  return count;
}

const initExportData = async (searchid) => {
  const result = {
    set_name: searchid,
    databaseValue: '',
    schemaValue: '',
    tableCountValue: 0,
  };
  const res = await getJobResults(searchid);
  const payload = res?.payload ?? {};
  const tables = extractTablesFromPayload(payload);
  result.tableCountValue = tables.length;
  result.databaseValue = String(payload.dbname || payload.database || '');
  result.schemaValue = String(payload.schema || result.databaseValue || 'public');
  return result;
}

const getTotalTableCount = async (searchid) => {
  const { tables } = await getOverview(searchid);
  return Array.isArray(tables) ? tables.length : 0;
}

const getPagedMetaData = async (searchid, pageSize, numPartitions, partitionNo) => {
  // console.log('getPagedMetaData');
  let response = {};

  try {
    const query = {
      "size": 0,
      "query": {

        "bool": {
          "should": [
            {
              "match_phrase": {
                "id" : searchid
              }
            },
            {
              "match_phrase": {
                "parent_searchid" : searchid
              }
            }
          ]
        }
      },
      "aggs": {
        "tables": {
          "terms": {
            "size": pageSize,
            "field": "table.keyword",
            "include": {
              "num_partitions": numPartitions,
              "partition": partitionNo
            }
          },
          "aggs": {
            "table_hits": {
              "top_hits": { "size": 1 }
            }
          }
        }
      }
    }
    response =  await API.post('gwapi','/elasticsearch/tables/_search', {
      body: query
      // queryStringParameters: {
      //   source: JSON.stringify(query),
      //   source_content_type: 'application/json'
      // }
     }
    );

    if (response && response.error) {
      throw new Error(response.error.reason || 'Error fetching ElasticSearch data.');
    }
  } catch(e) {
    console.log(e);

  }

  return response;
};

const getTableEntityCounts = async (searchid, from, pageSize, entity, searchTerms, sortBy, sortDir, totalBucketSize,connection, schemaSearchid) => {
  let entityQuery = null;
  let searchTermQuery = null;
  let filterAgg = null;
  let sortDirValue = sortDir || "desc";
  let sortKeyValue = { "nested_aggs>total_sensitive_entities": sortDirValue };

  let matchQuery = {
    "bool": {
      "should": [
        {
          "match_phrase": {
            "search": searchid
          }
        },
        {
          "match_phrase": {
            "parent_searchid": searchid
          }
        }
      ]
    }
  }

  if (searchTerms) {
    searchTermQuery = {
      "wildcard": {
        "table": `*${searchTerms}*`.toLowerCase()
      }
    }
  }

  if (entity && entity !== 'ALL') {
    entityQuery = {
      "nested": {
        "path": "entities",
        "query": {
          "term": {
            "entities.type": entity
          }
        }
      }
    };
  }

  if ((sortBy && sortBy !== 'overviewTotal' && sortBy !== 'table') && sortDirValue) {
    filterAgg = {
      "filter_by_entity": {
        "filter": { "term": { "entities.type": entity }},
        "aggs": {
          "entity_count": {
            "cardinality": {
              "field": "entities.unique_findings_key.hash",
              "precision_threshold": 40000
            }
          }
        }
      }
    };
    sortKeyValue = { "nested_aggs>filter_by_entity>entity_count": sortDirValue }
  }

  if (sortBy && sortBy === 'table') {
    sortKeyValue = { "_key": sortDirValue };
  }

  if (searchTermQuery || entityQuery) {
    matchQuery = {
      "bool": {
        "must": [
          {
            "bool": {
              "should": [
                {
                  "match_phrase": {
                    "search": searchid
                  }
                },
                {
                  "match_phrase": {
                    "parent_searchid": searchid
                  }
                }
              ]
            }
          },
          ...(entityQuery ? [entityQuery] : []),
          ...(searchTermQuery ? [searchTermQuery] : [])
        ]
      }
    }
  }

////////////
////////////

  // console.log('DEBUG CONNECTION', connection, "searchid", searchid)

//   const query = await test_query(schemaSearchid);
//   //
//   // let discMapping = await API.get('gwapi', '/elasticsearch/discovery/_mapping', {
//   //   queryStringParameters: {
//   //     source: JSON.stringify(query),
//   //     source_content_type: 'application/json'
//   //   }
//   // });


//   console.log('DEBUG QUERY', query)
//   let testResponse = await API.get('gwapi', '/elasticsearch/discovery/_search', {
//     queryStringParameters: {
//       source: JSON.stringify(query),
//       source_content_type: 'application/json'
//     }
//   });



//   console.log('DEBUG',searchid,testResponse)
//   // const hits = testResponse.hits.hits;
//   // let columns = [];
//   // let count = 0;
//   // let total_count = 0;
//   // let searchids = [];
//   // let parent_searchids = [];
//   // let hitlist = [];
//   // for (const hit of hits) {
//   //   let search= hit._source.search ? hit._source.search : "null";
//   //   let parent_searchid= hit._source.parent_searchid ? hit._source.parent_searchid : "null";
//   //   if (!searchids.includes(search)) {
//   //     searchids.push(search);
//   //   }
//   //   if (!parent_searchids.includes(parent_searchid)) {
//   //     parent_searchids.push(parent_searchid);
//   //   }
//   //   for (const entity of hit._source.entities) {
//   //     total_count++;
//   //     if (hit._source.analysis === 'ner' && entity.type === 'ADDRESS') {
//   //       if (!columns.includes(entity.column)) {
//   //         columns.push(entity.column);
//   //       }
//   //     }
//   //     if (entity.type === 'NAME') {
//   //       hitlist.push(hit._source);
//   //       count++;        
//   //     }
//   //   }
//   // }
//   // console.log('DEBUG','NAME COUNT:',count,'total_count:',total_count,'searchids:',searchids,'parent_searchids:',parent_searchids)
//   // console.log('DEBUG HITLIST',hitlist)
//   // console.log('DEBUG COLUMNS',columns)
// //////////
// ///////


  try {
    const query = {
      "size": 0,
      "query": matchQuery,
      "aggs": {
        "tables": {
          "terms": {
            "field": "table",
            "size": 20, // This limits to only 20 tables
            "order": sortKeyValue,
          },
          "aggs": {
            ...sortBy !== 'table' && ({
              "tables_bucket_sort": {
                "bucket_sort": {
                  "size": pageSize,
                  "from": from,
                }
              }
            }),
            "nested_aggs": {
              "nested": {
                "path": "entities"
              },
              "aggs": {
                ...filterAgg && ({ ...filterAgg }),
                "total_sensitive_entities": {
                  "cardinality": {
                    "field": "entities.unique_findings_key.hash",
                    "precision_threshold": 10000
                  }
                },
                "fields": {
                  "terms": {
                    "field": "entities.Column",
                    "size": 1000 // Increased from 100 to get more columns
                  },
                  "aggs": {
                    "entities": {
                      "terms": {
                        "field": "entities.type", 
                        "size": 1000 // Increased from 10 to get more entity types
                      },
                      "aggs": {
                        "count": {
                          "cardinality": {
                            "field": "entities.unique_findings_key.hash",
                            "precision_threshold": 10000
                          }
                        }
                      }
                    }
                  }
                }
              }
            },
          }
        }
      }
    };
    let response =  await API.post('gwapi','/elasticsearch/discovery/_search', {
      body: query
      // queryStringParameters: {
      //   source: JSON.stringify(query),
      //   source_content_type: 'application/json'
      // }
      }
    );
    // console.log('getTableEntityCounts response', response);

    if (response && response.error) {
      throw new Error(response.error.reason || 'Error fetching ElasticSearch data.');
    }
    return response;
  } catch(e) {
    console.log(e);
    throw e;
  }

}


const doesEntityExist = async (searchid) => {
  try {
    const query = {
      "size": 0,
      "query": {
        "bool": {
          "must": [
            {
              "match": {
                "search": searchid
              }
            },
            {
              "nested": {
                "path": "entities",
                "query": {
                  "terms": {
                    "entities.type": ["DRUG", "DIAGNOSIS", "INSURANCE", "IMMUNIZATION"]
                  }
                }
              }
            }
          ]
        }
      },
      "aggs": {
        "medical_entities": {
          "nested": {
            "path": "entities"
          },
          "aggs": {
            "medical_count": {
              "value_count": {
                "field": "entities.type"
              }
            }
          }
        }
      }
    };
    
    const result = await API.post('gwapi', '/elasticsearch/discovery/_search?request_cache=true', {
      body: query
    });
    // console.log('doesEnttransformCategoryData ityExist result', result);
    const medicalCount = result?.aggregations?.medical_entities?.medical_count?.value || 0;
    return {
      hasMedicalTerms: medicalCount > 0,
      medicalEntityCount: medicalCount,
      isPHI: medicalCount > 1000
    };
  } catch(e) {
    // console.log('doesEntityExist error', e);
    throw e;
  }
}

const getEntityBuckets = async (searchid, tables) => {
  // console.log('getEntityBuckets');
  let result = {};

  try {
    const query = {
      "size": 0,
      "query": {
        "bool": {
          "should": [
            {
              "match_phrase": {
                "search" : searchid
              }
            },
            {
              "match_phrase": {
                "parent_searchid" : searchid
              }
            }
          ]
        }
      },
      "aggs": {
        "tables": {
          "terms": {
            "field": "table",
            "size": tables.length,
            "include": tables
          },
          "aggs": {
            "nested_aggs": {
              "nested": {
                "path": "entities"
              },
              "aggs": {
                "entities": {
                  "terms": {
                    "field": "entities.type",
                    "size": 200
                  },
                  "aggs": {
                    "count": {
                      "cardinality": {
                        "field": "entities.unique_findings_key.hash",
                        "precision_threshold": 100
                      }
                    }
                  }
                }
              }
            },
          }
        }
      }
    };
    // console.log('getEntityBuckets query', query);
    result =  await API.post('gwapi','/elasticsearch/discovery/_search?request_cache=true', {
      body: query
      // queryStringParameters: {
      //   source: JSON.stringify(query),
      //   source_content_type: 'application/json'
      // }
     }
    );
    // console.log('getEntityBuckets response', result);
  } catch(e) {
    console.log(e);
  }

  return result;
}

const getTableSampledRowCounts = async (searchid, tables) => {
  // console.log('getTableSampledRowCounts');
  const tablesMatch = tables.map(table => ({
    "match_phrase": {
      "table.keyword": table
    }
  }));
  const query = {
    "size": 5,
    "query": {
      "bool": {
        "must": [
          {
            "bool": {
              "should": [
                {
                  "match_phrase": {
                    "id": searchid
                  }
                },
                {
                  "match_phrase": {
                    "search": searchid
                  }
                },
                {
                  "match_phrase": {
                    "parent_searchid": searchid
                  }
                }
              ]
            }
          },
          {
            "bool": {
              "should": tablesMatch
            }
          }
        ]
      }
    },
    "aggs": {
      "tables": {
        "terms": {
          "field": "table.keyword",
          "include": tables,
          "size": 20
        },
        "aggs": {
          "table_hits": {
            "top_hits": { "size": 1 }
          }
        }
      }
    }
  };
  // console.log('getTableSampledRowCounts query', query);
  const api = 'gwapi';
  const endpoint = '/elasticsearch/tables/_search';
  const params = { body: query };

  const response = await API.post(api, endpoint, params);
  // console.log('getTableSampledRowCounts response', response);
  const tableFieldsRowCount = transformTableEntityFields(response);
  // console.log('getTableSampledRowCounts tableFieldsRowCount', tableFieldsRowCount);
  return tableFieldsRowCount;
}

const getPagedEntityData = async (searchid, tables, pageSize) => {
  // console.log('getPagedEntityData');
  const tableFilters = tables || [];
  try {
    const query = {
      "size": 0,
      "query": {
          "match_phrase": {
            "search" : searchid
          }
      },
      "aggs": {
        "tables" : {
          "terms" : { 
            "field" : "table",
            ...tableFilters && ({"include": tableFilters}),
            "size": pageSize,
            "order": {"_key":"asc"}
          },
          "aggs": {
            "sensitive_columns": {
              "nested": {
                "path": "entities"
              },
              "aggs": {
                "sensitive_columns": {
                  "terms": {
                    "field": "entities.Column",
                    "size": 1000
                  },
                  "aggs": {
                    "entities": {
                      "terms": {
                        "field": "entities.type",
                        "size": 100,
                      },
                      "aggs": {
                        "count": {
                          "reverse_nested": {},
                          "aggs": {
                            "count_value": {
                              "cardinality": {
                                "field": "entities.unique_findings_key.hash"
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    };
  
    let response =  await API.post('gwapi','/elasticsearch/discovery/_search', {
      body: query
      // queryStringParameters: {
      //   source: JSON.stringify(query),
      //   source_content_type: 'application/json'
      // }
    }
    );
    // console.log('getPagedEntityData response', response);
    return response;
  } catch(e) {
    console.log(e);
    throw e;
  }
}

const getEntityAggData = async(searchid)=> {
  // console.log('getEntityAggData');
  try {
    const query = {
      "size": 10,
      "query": {
          "match_phrase": {
            "search" : searchid
          }
      },
      "aggs": {
        "tables" : {
          "terms" : { 
            "field" : "table",
            "size": 1000
          },
          "aggs": {
            "sensitive_columns": {
              "nested": {
                "path": "entities"
              },
              "aggs": {
                "sensitive_columns": {
                  "terms": {
                    "field": "entities.Column",
                    "size": 1000
                  },
                  "aggs": {
                    "entities": {
                      "terms": {
                        "field": "entities.type",
                        "size": 100,
                      },
                      "aggs": {
                        "count": {
                          "reverse_nested": {},
                          "aggs": {
                            "count_value": {
                              "cardinality": {
                                "script": "doc['sample_number'].value + '#' + doc['offset'].value"
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    };
  
    let response =  await API.post('gwapi','/elasticsearch/discovery/_search', {
      body: query
      // queryStringParameters: {
      //   source: JSON.stringify(query),
      //   source_content_type: 'application/json'
      // }
     }
    );
    return response;
  } catch(e) {
    console.log(e);
    throw e;
  }
}


const deleteLandscapeRisk = async (connectionName) => {
  // console.log('deleteLandcapeRisk');
  try {
    const api = 'gwapi';
    const endpoint = '/elasticsearch/landscape/_search';
    let query = {
      "query": {
        "bool": {
          "should": [
            {
              "match_phrase": {
                "identifier": connectionName
              }
            },
            {
              "match_phrase": {
                "name": connectionName
              }
            }
          ]
        }
      }
    };
    const params = { body: query };
    const response = await API.post(api, endpoint, params);
    // console.log('getLandscapeRiskData response', response);
  } catch (e) {
    console.log(e);
    throw e;
  }
}

const searchDelete = async (searchidToDelete, searchid ) => {

  // console.log('DEBUG searchDelete', searchidToDelete, searchid);
  await API.del('gwapi', '/search?searchid=' + searchid + '&parent_searchid=' + searchidToDelete);



  // console.log('searchDelete');
  let esBodyDiscovery = {
    "query": {
      "bool": {
        "should": [
          {
            "match_phrase": {
              "search": searchid
            }
          },
          {
            "match_phrase": {
              "parent_searchid": searchid
            }
          }
        ]
      }
    }
  };

  const discoveryResponse = await API.post('gwapi', '/elasticsearch/discovery/_delete_by_query', {
    body: esBodyDiscovery
  });
  // console.log('searchDelete', discoveryResponse);

  let esBodyTable = {
    "query": {
      "bool": {
        "should": [
          {
            "match_phrase": {
              "id": searchid
            }
          },
          {
            "match_phrase": {
              "parent_searchid": searchid
            }
          }
        ]
      }
    }
  };

  const tablesResponse = await API.post('gwapi', '/elasticsearch/tables/_delete_by_query', {
    body: esBodyTable 
  });
}

const searchIndex = async(query) => {
  // console.log('searchIndex');
  let results = await  API.post('gwapi','/elasticsearch/discovery/_search',{
    body: query
    // queryStringParameters: {
    //   source: JSON.stringify(query),
    //   source_content_type: 'application/json'
    //  }
  });
  return results
}

// response.data.aggregations.searches.buckets
const getSearchData = async(searchid) => {
  // console.log('getSearchData');
  try {
    return [
      await getMetaData(searchid),
      await getEntityAggData(searchid)
    ];
  } catch(e) {
    throw e;
  }
}

const checkTaskStatus = async (taskID) => {
  // console.log('checkTaskStatus');
  let result = {};

  try {
    const api = 'gwapi';
    const endpoint = `/elasticsearch/_tasks/${taskID}`;
    const response = await API.get(api, endpoint);
    const task = response.task || {};
    const status = task.status || {};
    // console.log('checkTaskStatus response', response);
    result = {
      completed: response.completed,
      total: status.total,
      updated: status.updated
    };
  } catch(e) {
    console.log(e);
    throw e;
  }

  return result;
}

/** Legacy ES index diagnostics; results were unused. Elasticsearch is removed from the app path. */
const countDiscoveryDocsWithMissingFields = async () => {};

/** Legacy ES index diagnostics; results were unused. Stopped calling `/elasticsearch/tables/_search` on every load. */
const countTableDocsWithMissingFields = async () => {};

const updateAllDiscoveryDocsByQuery = async (matchKey = {}) => {
  // console.log('updateAllDiscoveryDocsByQuery');
  let response = {};

  try {
    const api = 'gwapi';
    const endpoint = '/elasticsearch/discovery/_update_by_query?wait_for_completion=false';
    const params = {
      body: {
        "query": {
          "bool": {
            "must": [
              {
                "nested": {
                  "path": "entities",
                  "query": {
                    "bool": {
                      "should": [
                        {
                          "bool": {
                            "must_not": {
                              "exists": {
                                "field": "entities.unique_findings_key.hash"
                              }
                            }
                          }
                        },
                        {
                          "bool": {
                            "must_not": {
                              "exists": {
                                "field": "entities.table.hash"
                              }
                            }
                          }
                        },
                        {
                          "bool": {
                            "must_not": {
                              "exists": {
                                "field": "entities.unique_field_key.hash"
                              }
                            }
                          }
                        }
                      ]
                    }
                  }
                }
              }
            ]
          }
        },
        "script": {
          "source": `
            def entities = ctx._source['entities'];

            def sample_number = ctx._source['sample_number'];
            def offset = ctx._source['offset'];
            def table = ctx._source['table'];

            for (def entity : entities) {
              def col = entity.Column;
              def type = entity.type;
              def unique_findings_key = table + '#' + Integer.toString(col) + '#' + Integer.toString(sample_number) + '#' + Integer.toString(offset) + '#' + type;  
              def unique_field_key = table + '#' + Integer.toString(col);

              entity.unique_field_key = unique_field_key;
              entity.unique_findings_key = unique_findings_key;
              entity.table = table;
            }
          `,
          "lang": "painless"
        }
      }
    };
    response = await API.post(api, endpoint, params);
    // console.log('updateAllDiscoveryDocsByQuery response', response);

    try {
      const task = response.task;
      const taskStatus = await API.get(api, '/elasticsearch/_tasks/' + task);
      // console.log('updateAllDiscoveryDocsByQuery task status', taskStatus);
    } catch(e) {
      console.log(e);
    }

    if (response && response.error) {
      throw new Error(response.error.reason || 'Error fetching ElasticSearch data.');
    }
  } catch(e) {
    console.log(e);
    throw e;
  }

  return response;
}

// Updates mapping on /discovery index that are missing up-to-date fields.
const updateDiscoveryByQuery = async (matchKey = {}) => {
  // console.log('updateDiscoveryByQuery');
  let response = {};

  try {
    const api = 'gwapi';
    const endpoint = '/elasticsearch/discovery/_update_by_query?wait_for_completion=false';
    const params = { 
      body: {
        "query": {
          "bool": {
            "must": [
              matchKey,
              {
                "nested": {
                  "path": "entities",
                  "query": {
                    "bool": {
                      "should": [
                        {
                          "bool": {
                            "must_not": {
                              "exists": {
                                "field": "entities.unique_findings_key.hash"
                              }
                            }
                          }
                        },
                        {
                          "bool": {
                            "must_not": {
                              "exists": {
                                "field": "entities.table.hash"
                              }
                            }
                          }
                        }
                      ]
                    }
                  }
                }
              }
            ]
          }
        },
        "script": {
          "source": `
            def entities = ctx._source['entities'];

            def sample_number = ctx._source['sample_number'];
            def offset = ctx._source['offset'];
            def table = ctx._source['table'];

            for (def entity : entities) {
              def col = entity.Column;
              def type = entity.type;
              def unique_findings_key = table + '#' + Integer.toString(col) + '#' + Integer.toString(sample_number) + '#' + Integer.toString(offset) + '#' + type;  

              entity.unique_findings_key = unique_findings_key;
              entity.table = table;
            }
          `,
          "lang": "painless"
        }
      }
    };
    response = await API.post(api, endpoint, params);
    // console.log('updateDiscoveryByQuery response', response);

    if (response && response.error) {
      throw new Error(response.error.reason || 'Error fetching ElasticSearch data.');
    }
  } catch(e) {
    console.log(e);
    throw e;
  }

  return response;
}

// Updates mapping on /discovery index that are missing up-to-date fields.
const updateAllTableDocsByQuery = async () => {
  // console.log('updateAllTableDocsByQuery');
  let response = {};

  try {
    const api = 'gwapi';
    const endpoint = '/elasticsearch/tables/_search';
    const params = {
      body: {
        "size": 10,
        "query": {
          "bool": {
            // "must": {
            //   "match_phrase": {
            //     "id": "C2_Bucket_1697818086247"
            //   }
            // },
            "must_not": {
              "exists": {
                "field": "columns.unique_field_key"
              }
            }
          }
        }
      }
    };
    const countResponse = await API.post(api, endpoint, params);
    // console.log('updateAllTableDocsByQuery countResponse', countResponse);
  } catch(e) {
    console.log(e);
  }

  try {
    const api = 'gwapi';
    const endpoint = '/elasticsearch/tables/_update_by_query?wait_for_completion=false';
    const params = { 
      body: {
        "query": {
          "bool": {
            "must_not": {
              "exists": {
                "field": "columns.unique_field_key.hash"
              }
            }
          }
        },
        "script": {
          "source": `
            def table = ctx._source['table'];
            def columns = ctx._source['columns'];

            for (def column : columns) {
              def col = column.Field;
              def unique_field_key = table + '#' + col;
              column.unique_field_key = unique_field_key;
            }
          `,
          "lang": "painless"
        }
      }
    };
    response = await API.post(api, endpoint, params);
    // console.log('updateAllTablesDocsByQuery response', response);

    try {
      const task = response.task;
      const taskStatus = await API.get(api, '/elasticsearch/_tasks/' + task);
      // console.log('updateAllTablesDocsByQuery task status', taskStatus);
    } catch(e) {
      console.log(e);
    }

    if (response && response.error) {
      throw new Error(response.error.reason || 'Error fetching ElasticSearch data.');
    }
  } catch(e) {
    console.log(e);
    throw e;
  }

  return response;
}

const getMetaDataFilteredByTables = async (searchid, tables, size) => {
  // console.log('getMetaDataFilteredByTables');
  let result = {};

  try {
    const query = {
      "size": size,
      "query": {
        "match_phrase": {
          "id": searchid
        }
      },
      "aggs": {
        "tables": {
          "terms": {
            "field": "table.keyword",
            "include": tables,
            "size": tables.length
          },
          "aggs": {
            "table_hit": {
              "top_hits": {
                "size": 1
              }
            }
          }
        }
      }
    }
    result =  await API.post('gwapi','/elasticsearch/tables/_search', {
      body: query
      // queryStringParameters: {
      //   source: JSON.stringify(query),
      //   source_content_type: 'application/json'
      // }
      }
    );
  } catch(e) {
    console.log(e);
  }

  return result;
}

const getEntityDataFilteredByTables = async (searchid, tables, pageSize) => {
  // console.log('getEntityDataFilteredByTables');
  let result;

  try {
    const query = {
      "size": 0,
      "query": {
        "bool": {
          "must": [
            {
              "bool": {
                "should": [
                  {
                    "match_phrase": {
                      "search": searchid
                    }
                  },
                  {
                    "match_phrase": {
                      "parent_searchid": searchid
                    }
                  }
                ]
              }
            },
            {
              "terms": {
                "table": tables
              }
            }
          ]
        }
      },
      "aggs": {
        "tables": {
          "terms": {
            "field": "table",
            "size": (tables && tables.length || 0)
          },
          "aggs": {
            "sensitive_columns": {
              "nested": {
                "path": "entities"
              },
              "aggs": {
                "sensitive_columns": {
                  "terms": {
                    "field": "entities.Column",
                    "size": 200
                  },
                  "aggs": {
                    "entities": {
                      "terms": {
                        "field": "entities.type",
                        "size": 200,
                      },
                      "aggs": {
                        "count": {
                          "cardinality": {
                            "field": "entities.unique_findings_key.hash",
                            "precision_threshold": 10
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    };
    // console.log('getExportData query', query);
    result =  await API.post('gwapi','/elasticsearch/discovery/_search', {
      body: query
      // queryStringParameters: {
      //   source: JSON.stringify(query),
      //   source_content_type: 'application/json'
      //  }
    }
    );
    // console.log('getExportData result', result);
    // console.log('getEntityDataFilteredByTables response', result);

    if (result && result.error) {
      throw new Error(result.error.reason || 'Error fetching export data');
    }
  } catch(e) {
    console.log(e);
    throw e;
  }

  return result;
}

const getHitsFromMetaData = (pagedMetaData) => {
  // console.log('getHitsFromMetaData');
  let result = [];

  try {
    const aggs = pagedMetaData && pagedMetaData.aggregations || {};
    const tableBuckets = aggs && aggs.tables && aggs.tables.buckets || [];
    const hits = tableBuckets.map((bucket) => {
      const hits = bucket && bucket.table_hits && bucket.table_hits.hits && bucket.table_hits.hits.hits || [];
      return hits.map(hit => hit._source);
    });
    result = hits.flat();
  } catch(e) {
    console.log(e);
    throw e;
  }
  return result;
}

const getExportData = async ({ searchid, pageSize }, _numPartitions, partitionNo) => {
  const overview = await getOverview(searchid);
  const tables = overview.tables || [];
  const start = partitionNo * pageSize;
  const slice = tables.slice(start, start + pageSize);
  const tablesValue = slice.map((t) => ({
    name: t.name,
    columns: (t.fields || []).map((f) => ({
      column_name: f.name,
      data_type: f.data_type || 'string',
      found_entities: f.findings && typeof f.findings === 'object' ? f.findings : {},
    })),
  }));
  return { tablesValue };
}

const loadSearchResults = async (from) => {
  // console.log('loadSearchResults');
  const searches = await getSearchesPage(from);
  const results = transformSearchResultsPage(searches);
  return results;
}

const transformSearchResultsPage = (sources) => {
  // console.log('transformSearchResultsPage');
  const allSearchResults = [];
  const connectionMap = new Map();

  // First pass: collect all search results (both parent and child items)
  sources.forEach(source => {
    const parentSearch = source.key;
    const timeStamp = source.time_stamp.value;
    const hit = source.top_hit.hits.hits[0];
    const parentSource = hit._source;

    const parentItem = {
      searchid: parentSearch,
      date: timeStamp,
      connection: parentSource.connection_name,
      type: parentSource.engine,
      displayId: parentSource.rules.searchName,
      sampleRules: parentSource.rules && parentSource.rules.name
    };
    allSearchResults.push(parentItem);

    const childBuckets = source.child_searchid_filter.child_searchid_list.buckets;

    if (childBuckets && childBuckets.length > 1) {
      childBuckets.forEach(bucket => {
        const childSearch = bucket.key;
        const childHit = bucket.top_hit.hits.hits[0];
        const childSource = childHit._source;
        const childItem = {
          searchid: childSearch,
          date: timeStamp,
          connection: childSource.connection_name,
          type: childSource.engine,
          displayId: getDisplayId(childSearch),
          parent_searchid: childSource.parent_searchid,
          sampleRules: childSource.rules && childSource.rules.name
        };
        allSearchResults.push(childItem);
      });
    }
  });

  // Group search results by connection
  allSearchResults.forEach(item => {
    const connectionName = item.connection;
    if (!connectionMap.has(connectionName)) {
      connectionMap.set(connectionName, []);
    }
    connectionMap.get(connectionName).push(item);
  });

  // Build result array with connection parents and their children
  const result = [];
  const connections = Array.from(connectionMap.keys());

  // Sort connections by most recent date (descending)
  connections.sort((connA, connB) => {
    const itemsA = connectionMap.get(connA);
    const itemsB = connectionMap.get(connB);
    const maxDateA = Math.max(...itemsA.map(item => item.date));
    const maxDateB = Math.max(...itemsB.map(item => item.date));
    return maxDateB - maxDateA;
  });

  connections.forEach(connectionName => {
    const connectionItems = connectionMap.get(connectionName);
    
    // Sort items within connection by date (most recent first)
    connectionItems.sort((a, b) => b.date - a.date);

    // Get connection metadata from first item (most recent)
    const mostRecentItem = connectionItems[0];
    const connectionParentId = `connection_${connectionName}`;

    // Create connection-level parent item with most recent values
    const connectionParent = {
      searchid: connectionParentId,
      connection: connectionName,
      type: mostRecentItem.type,
      date: mostRecentItem.date, // Most recent date
      displayId: mostRecentItem.displayId, // Most recent display ID
      sampleRules: mostRecentItem.sampleRules, // Most recent sampling rules
      mostRecentSearchId: mostRecentItem.searchid, // Store actual searchid for linking
      isConnectionParent: true
    };
    result.push(connectionParent);

    // Add all search results EXCEPT the most recent one as children
    // (skip index 0, start from index 1)
    for (let i = 1; i < connectionItems.length; i++) {
      const childItem = {
        ...connectionItems[i],
        parent_connection: connectionParentId
      };
      result.push(childItem);
    }
  });

  return result;
}

const getSearchesPage = async (from) => {
  // console.log('getSearchesPage');
  const searchListAggregationQuery = {
    "size": 0,
    "query": {
      "match_all": {}
    },
    "aggs": {
      "searchid_list": {
        "terms": {
          "script": "if (doc['parent_searchid.keyword'].size() != 0) { return doc['parent_searchid.keyword'].value } else { return doc['id.keyword'].value }",
          "order": { "time_stamp": "desc" },
          "size": 1000
        },
        "aggs": {
          "bucket_sort": {
            "bucket_sort": {
              "from": from,
              "size": 10
            }
          },
          "top_hit": {
            "top_hits": {
              "size": 1
            }
          },
          "time_stamp": {
            "max": {
              "field": "time_stamp"
            }
          },
          "child_searchid_filter": {
            "filter": {
              "bool": {
                "must": {
                  "exists": {
                    "field": "parent_searchid"
                  }
                }
              }
            },
            "aggs": {
              "child_searchid_list": {
                "terms": {
                  "field": "id.keyword",
                  "size": 100
                },
                "aggs": {
                  "top_hit": {
                    "top_hits": {
                      "size": 1
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  };
  const api = 'gwapi';
  const endpoint = '/elasticsearch/tables/_search';
  const params = { body: searchListAggregationQuery };
  const response = await API.post(api, endpoint, params);
  // console.log('getSearchesPage response', response);
  const searchBuckets = response.aggregations.searchid_list.buckets;
  return searchBuckets;
}

const computeScore = async(connectionName, exportJSON, searchid, searchDate) => {
  // console.log('computeScore');
  let result = {};

  // Calculate score based off results
  try {
    const queryStringParameters = {
      connection_name: connectionName,
      search_id: searchid,
      search_date: searchDate
    };
    const response = await API.put('gwapi', '/scores', { 
      body: exportJSON,
      queryStringParameters: queryStringParameters 
    });
    result = { ...response }
  } catch(e) {
    console.log(e);
    throw e;
  }

  return result;
}

const getMaxScoreByID = async (searchid) => {
  // console.log('getMaxScoreByID');
  let result = null;

  try {
    const maxScoreAggregationQuery = {
      "size": 0,
      "query": {
        "match_phrase": {
          "search_id" : searchid
        }
      },
      "aggs": {
        'max_score': {
          'nested': {
            'path': 'scores'
          },
          'aggs': {
            'max_score': {
              'max': {
                'field': 'scores.score'
              }
            }
          }
        }
      }
    };

    const response = await API.post('gwapi', '/elasticsearch/scores/_search', {
      body: maxScoreAggregationQuery 
    });
    let maxScoreValue = null;

    try {
      maxScoreValue = response.aggregations.max_score.max_score.value;
    } catch(e) {
      console.log(e);
    }
    
    if (maxScoreValue) {
      result = maxScoreValue;
    }
  } catch(e) {
    console.log(e);
    throw e;
  }

  return result;
}

const getScoreByID = async(searchid) => {
  // console.log('getScoreByID');
  let result = {};

  try {
    const scoreSearchQuery = {
      "size": 2000,
      "query": {
          "match_phrase": {
            "search" : searchid
          }
      }
    };
    let response = await API.post('gwapi','/elasticsearch/scores/_search', {
      body: scoreSearchQuery
      // queryStringParameters: {
      //   source: JSON.stringify(scoreSearchQuery),
      //   source_content_type: 'application/json'
      // }
    });
    result = response;
  } catch(e) {
    console.log(e);
    throw e;
  }

  return result;
}

const getDisplayID = (searchResult, prevDate, index) => {
  // console.log('getDisplayID');
  try {
    let date = new Date(searchResult.date);
    let monthDate = date.getDate();
    let dateIndex = searchResult.searchid.lastIndexOf('_');
    let displayId = searchResult.searchid.slice(0, dateIndex);

    if (prevDate !== monthDate) {
      prevDate = monthDate;
      index = 0;
    } else {
      index++;
    }

    displayId += ' ' + ((parseInt(date.getMonth())) + 1); 
    displayId += '.' + monthDate;
    displayId += '.' + date.getFullYear().toString().substr(-2);
    displayId += '.' + index; 

    return displayId;
  } catch(e) {
    console.log(e);
    return searchResult.searchid;
  }
}

// Accepts either a numeric id (or numeric string) or a connection name. Numeric
// resolves by id; non-numeric falls back to name lookup. Lets the editor route
// carry an id while older name-based deep links keep working unchanged.
const getConnectionData = async (id) => {
  const numericId = Number(id);
  if (!Number.isFinite(numericId)) {
    return {};
  }
  let dto;
  try {
    dto = await getDataSourceById(numericId);
  } catch (e) {
    return {};
  }
  if (!dto || dto.id == null) {
    return {};
  }
  let ui = toUiDataSource(dto);
  const cid = getDataSourceNumericId(ui);
  if (cid != null) {
    try {
      const testDto = await getLastDataSourceTest(cid);
      const summary = testDtoToUiSummary(testDto, ui.name);
      ui = {
        ...ui,
        ...summary,
        connectionTest: testDto.tested ? summary : undefined,
      };
    } catch (e) {
      console.log(e);
    }
    ui = await mergeScheduleIntoUiRow(ui);
  }
  return ui;
}

const getTestResults = async (connection) => {
  try {
    const cid = getDataSourceNumericId(connection);
    if (cid == null) {
      throw new Error('Missing data source id');
    }
    const dto = await getLastDataSourceTest(cid);
    const summary = testDtoToUiSummary(dto, connection.name);
    return {
      job: { JobRuns: [] },
      results: summary,
    };
  } catch (e) {
    console.log(e);
    throw e;
  }
}

const getPreviousSearchMeta = async () => {
  // console.log('getPreviousSearchMeta');
  let searchidValue = '';
  let dateValue = null;
  let engineValue = '';
  
  try {
    const query = {
      "size": 1,
      "query": {
        "match_all": {}
      },
      "sort": [
        {
          "time_stamp": {
              "order": "desc"
          }
        }
      ]
    };
  
    const response =  await API.post('gwapi','/elasticsearch/tables/_search', {
      body: query
      // queryStringParameters: {
      //   source: JSON.stringify(query),
      //   source_content_type: 'application/json'
      // }
    });
    const hits = response && response.hits && response.hits.hits || [];
    const source = hits[0] && hits[0]._source;
    if (source) {
      const id = source.parent_searchid || source.id;
      searchidValue = id;
      dateValue = source.time_stamp;
      engineValue = source.engine;
    }
  } catch(e) {
    console.log(e);
    throw e;
  }

  return {
    searchidValue, dateValue, engineValue
  };
}

const getSearchStatuses = async (params) => {
  let result = [];

  try {
    const api = 'gwapi';
    const path = '/status';
    const response = await API.get(api, path);
    const statusList = response.statusList || [];
    result = transformStatuses(statusList);
    // console.log('DEBUGgetSearchStatuses result', result);
  } catch(e) {
    console.log(e);
  }

  return result;
}


// if a parent_searchid has only 1 child- merge them.
const transformStatuses = (statusList) => {
  const result = [];
  const parents = [];
  const children = [];

  statusList.forEach((status, i) => {
    if (status.child_searchid_count === 1) {
      parents.push({ ...status, index: i });
    }
    const state = getSearchState(status);
    const processed = getRowsProcessed(status);
    status.state = state;
    if (processed) status.total_processed = processed;
  });

  parents.forEach((parent) => {
    const searchid = parent.searchid;
    const parentIndex = parent.index;
    const childIndex = statusList.findIndex(status => status.parent_searchid === searchid);
    statusList[parentIndex] = {
      ...statusList[parentIndex],
      ...statusList[childIndex],
      searchid: searchid
    };
    delete statusList[parentIndex].parent_searchid;
    children[childIndex] = true;
  });

  statusList.forEach((status, i) => { if (!children[i]) result.push(status); });

  return result;
}

const startSearch = async (connection, rules, searchName) => {
  // console.log('startSearch');
  let response = {};

  try {
    const target = getGlueTargetData(connection, rules);
    target.searchName = searchName;
    const api = 'gwapi';
    const endpoint = '/connect';
    const params = { body: target };
    response = await API.post(api, endpoint, params);

    localStorage.setItem('lastConnection', connection.name);
    localStorage.setItem('lastRules', rules.id);
  } catch(e) {
    console.log(e);
    throw e;
  }

  return response;
}

const getLandscapeData = async() => {
  let result = {};

  try {
    const api = 'gwapi';
    const endpoint = '/elasticsearch/landscape/_search';
    const query = {
      "size": 1,
      "query": {
        "match_phrase": {
          "type": "scanned_sources"

          // "identifier": "sugarcrm-encrypted-b"
        }
      },
      "sort": [
        {
          "time_stamp": {
            "order": "desc"
          }
        }
      ]
    };
    const params = { body: query };
    const response = await API.post(api, endpoint, params);
    // console.log('getLandscapeData response', response);

    const landscape = response && response.hits && response.hits.hits && response.hits.hits[0] && response.hits.hits[0]._source;
    if (landscape) {
      result = landscape;
    }
    // const sources = landscape && landscape.sources;
    // if (sources) result = sources;
  } catch(e) {
    console.log(e);
    throw e;
  }
  // console.log('getLandscapeData result', result);
  return result;
}

const getTotalDiscoveryReports = async () => {
  // console.log('getTotalDiscoveryReports');
  const query = {
    "query": {
      "match_all": {}
    },
    "aggs": {
      "total_report_count": {
        "cardinality": {
          "field": "id.keyword"
        }
      }
    }
  };

  const api = 'gwapi';
  const endpoint = '/elasticsearch/tables/_search';
  const params = { body: query };
  const response = await API.post(api, endpoint, params);
  return response.aggregations.total_report_count.value;
}

const emptySources = {
  "name": "UPDATING_LANDSCAPE",
  "time_stamp": 1692189137140,
  "sources": []
};

const getUndiscoveredSources = async () => {
  // console.log('getUndiscoveredSources');
  try {
    const query = {
      "size": 1,
      "query" : {
        "match_phrase": {
          "type": "unscanned_sources"
        }
      },
      "sort": [
        {
          "time_stamp": {
            "order": "desc"
          }
        }
      ]
    }

    let response =  await API.post('gwapi','/elasticsearch/landscape/_search', {
      body: query
      // queryStringParameters: {
      //   source: JSON.stringify(query),
      //   source_content_type: 'application/json'
      // }
    });
    // console.log('getUndiscoveredSources response', response);
    const hit = response.hits && response.hits.hits && response.hits.hits[0];
    // const sources = hit && hit._source && hit._source.sources;

    // if (sources) {
    //   return sources;
    // } else {
    //   return emptySources
    // }
    if (hit) {
      return hit._source;
    } else {
      return emptySources;
    }
  } catch(e) {
    console.log(e);
    return emptySources;
  }
}

const startLandscapeDiscovery = async() => {
  // console.log('startLandscapeDiscovery');
  try {
    const api = 'gwapi';
    const endpoint = '/datasource/unscanned';
    const response = await API.post(api, endpoint);
    // console.log('startLandscapeDiscovery response', response);
    if (response)
      return response.landscape;
    else {
      return  {
        "name": "unscanned_sources_1692189137140",
        "timestamp": 1692189137140,
        "sources": []};
    }
  } catch(e) {
    console.log(e);
    throw e;
  }
}

const testAggs = async (searchid) => {

  try {
    const query = {
      size: 0,
      aggs: {
        "tables": {
          "terms": { "field": "table" }
        }
      }
    };

    let response = await API.post('gwapi', '/elasticsearch/tables/_search', {
      body: query
      // queryStringParameters: {
      //   source: JSON.stringify(query),
      //   source_content_type: 'application/json'
      // }
    });
    return response;
  } catch (e) {
    console.log(e);
    throw e;
  }
}

export { 
  computeScore,
  countDiscoveryDocsWithMissingFields,
  countTableDocsWithMissingFields,
  checkTaskStatus,
  testAggs,
  getRulesList, 
  saveRules,
  getConnectionData,
  getOverviewData,
  getDataSources,
  getDisplayID,
  getExportData,
  getEntityBuckets,
  getFoundEntities,
  getLandscapeData,
  getUndiscoveredSources,
  getMetaData,
  getTableMetaData,
  getPagedMetaData,
  getPagedEntityData,
  getPreviousSearchMeta,
  getTableEntityCounts,
  getTotalDiscoveryReports,
  getScoreByID,
  getSearchStatuses,
  getTableSampledRowCounts,
  getTestResults,
  getTotalTableCount,
  getTotalSensitiveTableCount,
  getMaxScoreByID,
  initExportData,
  testDataSource,
  deleteDataSource,
  getLastConnection,
  getLastRules,
  getSearchData,
  saveDataSource,
  searchIndex, 
  searchDelete,
  startSearch,
  startLandscapeDiscovery,
  updateDataSource,
  loadSearchResults,
  deleteLandscapeRisk,
  doesEntityExist
} 